import { MixerHorizontalIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, IconButton, Popover } from '@radix-ui/themes';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { SCALING_OPTIONS } from 'enums/pitches';
import { t } from 'i18next';
import { IImportPitcherConfigurationRow } from 'interfaces/i-game-data';
import { IScalingConfiguration } from 'interfaces/i-pitch-scaling';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { clone } from 'lodash';
import { FC, useState } from 'react';

interface IProps {
  row: IImportPitcherConfigurationRow;
  processing: boolean;
  onApplyAll: (value: IScalingConfiguration) => void;
  onApplyOne: (playerPk: number, value: IScalingConfiguration) => void;
}

export const ImportPitchersScalingPopover: FC<IProps> = (props) => {
  // necessary for reflecting the user's changes prior to clicking apply/apply to all
  const [tempScaling, setTempScaling] = useState(clone(props.row.scaling));

  const [open, setOpen] = useState(false);

  const isScaling = ![
    props.row.scaling.speed,
    props.row.scaling.breakZ,
    props.row.scaling.breakZ,
  ].every((n) => n === 1);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          // on (re-)open, reset the scaling back to whatever was last saved for the row
          setTempScaling(clone(props.row.scaling));
        }

        setOpen(nextOpen);
      }}
    >
      <Popover.Trigger>
        <IconButton
          className={isScaling ? undefined : 'btn-floating'}
          variant={
            isScaling
              ? RADIX.BUTTON.VARIANT.SECONDARY
              : RADIX.BUTTON.VARIANT.BORDERLESS
          }
          color={isScaling ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL}
          disabled={props.processing}
        >
          <MixerHorizontalIcon />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content asChild>
        <Card
          style={{
            padding: 'var(--space-3)',
          }}
        >
          <CommonFormGrid columns={1}>
            <Box>
              <CommonSelectInput
                id="scale-speed"
                name="scale-speed"
                label="common.speed"
                options={SCALING_OPTIONS}
                value={tempScaling.speed.toString()}
                onNumericChange={(v) =>
                  setTempScaling((prev) => ({
                    ...prev,
                    speed: v,
                  }))
                }
                disabled={props.processing}
                skipSort
              />
            </Box>

            <Box>
              <CommonSelectInput
                id="scale-break-z"
                name="scale-break-z"
                label="common.v-break"
                options={SCALING_OPTIONS}
                value={tempScaling.breakZ.toString()}
                onNumericChange={(v) =>
                  setTempScaling((prev) => ({
                    ...prev,
                    breakZ: v,
                  }))
                }
                disabled={props.processing}
                skipSort
              />
            </Box>

            <Box>
              <CommonSelectInput
                id="scale-break-x"
                name="scale-break-x"
                label="common.h-break"
                options={SCALING_OPTIONS}
                value={tempScaling.breakX.toString()}
                onNumericChange={(v) =>
                  setTempScaling((prev) => ({
                    ...prev,
                    breakX: v,
                  }))
                }
                disabled={props.processing}
                skipSort
              />
            </Box>

            <Flex gap="1">
              <Button
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                disabled={props.processing}
                onClick={() => {
                  props.onApplyAll(tempScaling);
                  setOpen(false);
                }}
              >
                {t('common.apply-all')}
              </Button>
              <Button
                disabled={props.processing}
                onClick={() => {
                  props.onApplyOne(props.row.pitcher.playerPk, tempScaling);
                  setOpen(false);
                }}
              >
                {t('common.apply')}
              </Button>
            </Flex>
          </CommonFormGrid>
        </Card>
      </Popover.Content>
    </Popover.Root>
  );
};
